import { t, useLocale, getLocale } from "@/i18n/locale";
import { GenericAppGlyph } from "@/components/personal-apps/AppImage";
import { capsuleDetailsTarget, capsuleTarget, detailsNavigationTarget } from "@/components/personal-apps/discovery/discoveryUrl";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  AppWindow,
  Clock,
  FolderGit2,
  GitBranch,
  LayoutGrid,
  Monitor,
  Search,
  Settings,
  SquareTerminal,
  X,
} from "lucide-react";
import { listCapsules, type CapsuleSummary } from "@/api/storeClient";
import { isStoreRef, loadLibrary } from "@/api/libraryClient";
import { humanizeCategory } from "@/components/runs/feedItem";
import { useSearch } from "./SearchProvider";
import { parseGitRepoUrl } from "./githubUrl";
import { GIT_HOST_CONFIGS } from "@/lib/repoSource/gitHosts";

type PaletteRail = "all" | "apps" | "categories" | "surfaces" | "actions";

type PaletteOption = {
  id: string;
  group: string;
  title: string;
  sub?: string;
  iconUrl?: string | null;
  icon?: ReactNode;
  mono?: string;
  /** New-tab target for Cmd/Ctrl+Enter, where applicable. */
  href?: string;
  activate: () => void;
};

const RESULT_LIMIT = 12;
const CATEGORY_FETCH_LIMIT = 20;
const RECENT_LIMIT = 6;

const SURFACE_ROWS: { id: string; label: string; icon: ReactNode }[] = [
  { id: "", label: "All surfaces", icon: <LayoutGrid size={15} aria-hidden /> },
  { id: "web_ui", label: "Web", icon: <Monitor size={15} aria-hidden /> },
  {
    id: "desktop_ui",
    label: "Desktop",
    icon: <AppWindow size={15} aria-hidden />,
  },
  {
    id: "cli",
    label: "Terminal",
    icon: <SquareTerminal size={15} aria-hidden />,
  },
];

function isDescendantScrollKey(key: string): boolean {
  return (
    key === "ArrowDown" || key === "ArrowUp" || key === "Home" || key === "End"
  );
}

type GlobalCommandPaletteProps = {
  /** Current route — surface picks navigate Home when not already there. */
  route: string;
  onNavigate: (
    route: string,
    opts?: { source?: string; runId?: string; pick?: boolean },
  ) => void;
  signedIn: boolean;
};

/**
 * The Discover search palette (DESIGN.md §GlobalCommandPalette): "/" or its
 * trigger opens a centered, blurred-overlay dialog with a left category rail
 * and an internal-scroll results panel. ⌘K / Ctrl+K is NOT handled here —
 * it belongs to the My Apps launcher. Search hits the existing
 * `listCapsules` endpoint (aborted + stale-guarded), a pasted GitHub URL
 * becomes an Add App candidate (the retired public repo-intake pages are
 * gone), and every action closes the palette BEFORE navigating (a
 * launch/consent flow never stacks on top of it).
 */
export default function GlobalCommandPalette({
  route,
  onNavigate,
  signedIn,
}: GlobalCommandPaletteProps) {
  useLocale();
  const { open, closePalette, query, setQuery, debouncedQuery, setSurface } =
    useSearch();

  const [rail, setRail] = useState<PaletteRail>("all");
  const [results, setResults] = useState<CapsuleSummary[]>([]);
  const [categoryPool, setCategoryPool] = useState<CapsuleSummary[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<Element | null>(null);
  const requestSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const go = useCallback(
    (
      target: string,
      opts?: { source?: string; runId?: string; pick?: boolean },
    ) => {
      closePalette();
      if (opts) onNavigate(target, opts);
      else onNavigate(target);
    },
    [closePalette, onNavigate],
  );

  const openApp = useCallback((slug: string, publisher?: string) => go(detailsNavigationTarget(capsuleTarget(slug, publisher), "overview", window.location.pathname + window.location.search)), [go]);

  // ── Open/close lifecycle: focus in, focus restore, inert background,
  //    body scroll lock (DESIGN.md §Accessibility contract). ──
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement;
    const rootEl = document.getElementById("root");
    rootEl?.setAttribute("inert", "");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the search input on open.
    inputRef.current?.focus();
    return () => {
      rootEl?.removeAttribute("inert");
      document.body.style.overflow = prevOverflow;
      const el = restoreFocusRef.current;
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
  }, [open]);

  // Reset the per-open view state.
  useEffect(() => {
    if (open) {
      setRail("all");
      setCategoryFilter(null);
      setActiveIndex(0);
    }
  }, [open]);

  // ── Search: debounced query comes from SearchProvider (shared with the
  //    Home grid). Abort the previous request on every new query and never
  //    render a stale response (DESIGN.md §Search behavior). ──
  useEffect(() => {
    if (!open) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++requestSeqRef.current;
    setSearching(true);
    listCapsules({
      q: debouncedQuery || undefined,
      limit: debouncedQuery ? RESULT_LIMIT : CATEGORY_FETCH_LIMIT,
      signal: controller.signal,
    })
      .then((res) => {
        if (controller.signal.aborted || seq !== requestSeqRef.current) return;
        setResults(res.capsules);
        if (!debouncedQuery) setCategoryPool(res.capsules);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return; // aborted ≠ error (DESIGN.md)
        if (seq !== requestSeqRef.current) return;
        console.error("Palette search failed:", err);
        setResults([]);
      })
      .finally(() => {
        if (!controller.signal.aborted && seq === requestSeqRef.current) {
          setSearching(false);
        }
      });
    return () => controller.abort();
  }, [open, debouncedQuery]);

  // A wider pool for the Categories rail while a query is active (the main
  // result list stays at RESULT_LIMIT).
  useEffect(() => {
    if (!open || !debouncedQuery) return;
    const controller = new AbortController();
    listCapsules({
      q: debouncedQuery,
      limit: CATEGORY_FETCH_LIMIT,
      signal: controller.signal,
    })
      .then((res) => {
        if (!controller.signal.aborted) setCategoryPool(res.capsules);
      })
      .catch(() => {
        /* categories are optional */
      });
    return () => controller.abort();
  }, [open, debouncedQuery]);

  // ── Option model ────────────────────────────────────────────
  const gitRepo = useMemo(() => parseGitRepoUrl(query), [query]);

  const recentApps = useMemo(() => {
    if (debouncedQuery) return [];
    try {
      return loadLibrary()
        .filter((a) => isStoreRef(a.source))
        .slice(0, RECENT_LIMIT);
    } catch {
      return [];
    }
  }, [debouncedQuery, open]); // eslint-disable-line react-hooks/exhaustive-deps -- re-read per open

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of categoryPool) {
      const cat = c.primary_category || c.primaryCategory || "";
      if (cat) counts.set(cat, (counts.get(cat) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [categoryPool]);

  const appOption = useCallback(
    (c: CapsuleSummary, group: string): PaletteOption => ({
      id: `app:${c.id}`,
      group,
      title: c.name,
      sub: (c.short_description || c.description || "").trim() || undefined,
      iconUrl: (c.store_icon || c.icon || "").trim() || null,
      href: `${window.location.origin}${capsuleDetailsTarget(c.slug, c.publisher?.handle)}`,
      activate: () => openApp(c.slug, c.publisher?.handle),
    }),
    [openApp],
  );

  const visibleOptions = useMemo<PaletteOption[]>(() => {
    const options: PaletteOption[] = [];
    const includeApps = rail === "all" || rail === "apps";
    const includeActions = rail === "all" || rail === "actions";

    if (gitRepo) {
      const hostConfig = GIT_HOST_CONFIGS[gitRepo.provider];
      const HostIcon = hostConfig.icon;
      options.push({
        id: `${gitRepo.provider}:${gitRepo.owner}/${gitRepo.repo}`,
        group: hostConfig.label,
        title: t("Add {{value0}}/{{value1}}", { value0: gitRepo.owner, value1: gitRepo.repo }),
        sub: "Connect this repository as an app source",
        icon: <HostIcon size={16} aria-hidden />,
        mono: `${gitRepo.host}/${gitRepo.owner}/${gitRepo.repo}`,
        // The retired public /github.com/<owner>/<repo> intake pages are gone;
        // a detected repo now routes into Add App, where the source URL is
        // connected. The repo import logic itself lives on in the submission
        // flow and its clients.
        activate: () =>
          go("/apps/new", {
            source: hostConfig.webUrl(gitRepo.owner, gitRepo.repo),
          }),
      });
    }

    if (rail === "surfaces") {
      for (const row of SURFACE_ROWS) {
        options.push({
          id: `surface:${row.id || "all"}`,
          group: "Surfaces",
          title: t(row.label),
          sub: "Filter the Home gallery",
          icon: row.icon,
          activate: () => {
            setSurface(row.id);
            closePalette();
            if (route !== "/") onNavigate("/");
          },
        });
      }
      return options;
    }

    if (rail === "categories") {
      for (const [cat, count] of categories) {
        options.push({
          id: `cat:${cat}`,
          group: "Categories",
          title: humanizeCategory(cat),
          sub: `${count} ${count === 1 ? "app" : "apps"}`,
          icon: <FolderGit2 size={15} aria-hidden />,
          activate: () => {
            setCategoryFilter(cat);
            setRail("apps");
          },
        });
      }
      return options;
    }

    if (includeApps) {
      const filtered = categoryFilter
        ? results.filter(
            (c) =>
              (c.primary_category || c.primaryCategory || "") ===
              categoryFilter,
          )
        : results;
      if (debouncedQuery) {
        for (const c of filtered) {
          options.push(
            appOption(
              c,
              categoryFilter ? humanizeCategory(categoryFilter) : "Capsules",
            ),
          );
        }
      } else {
        for (const app of recentApps) {
          const segments = app.source.split("/");
          const slug = segments.at(-1) || app.source;
          const publisher = segments.length === 2 ? segments[0] : undefined;
          options.push({
            id: `recent:${app.source}`,
            group: "Recent",
            title: app.name,
            sub: app.source,
            iconUrl: app.iconUrl,
            icon: <Clock size={14} aria-hidden />,
            href: `${window.location.origin}${capsuleDetailsTarget(slug, publisher)}`,
            activate: () => openApp(slug, publisher),
          });
        }
        for (const c of filtered) {
          // Popular (server default ordering) — skip anything already Recent.
          if (recentApps.some((a) => a.source.endsWith(`/${c.slug}`))) continue;
          options.push(appOption(c, "Popular"));
        }
      }
    }

    if (includeActions) {
      options.push({
        id: "action:git-url",
        group: "Actions",
        title: "Paste a GitHub or Codeberg URL to add an app",
        icon: <GitBranch size={15} aria-hidden />,
        activate: () => inputRef.current?.focus(),
      });
      options.push({
        id: "action:settings",
        group: "Actions",
        title: "Open Settings",
        icon: <Settings size={15} aria-hidden />,
        activate: () => go("/settings"),
      });
    }

    return options;
  }, [
    rail,
    gitRepo,
    results,
    recentApps,
    categories,
    categoryFilter,
    debouncedQuery,
    signedIn,
    appOption,
    openApp,
    go,
    setSurface,
    closePalette,
    onNavigate,
    route,
    getLocale(),
  ]);

  // Clamp the active option when the list changes.
  useEffect(() => {
    setActiveIndex((i) =>
      Math.min(Math.max(i, 0), Math.max(visibleOptions.length - 1, 0)),
    );
  }, [visibleOptions.length]);

  const scrollActiveIntoView = useCallback((index: number) => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${index}"]`,
    );
    try {
      el?.scrollIntoView({ block: "nearest" });
    } catch {
      // jsdom has no scrollIntoView — keyboard nav still works there.
    }
  }, []);

  const moveActive = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(next, visibleOptions.length - 1));
      setActiveIndex(clamped);
      scrollActiveIntoView(clamped);
    },
    [visibleOptions.length, scrollActiveIntoView],
  );

  // ── Keyboard: combobox keys stay on the input (DOM focus never leaves
  //    it — candidates move via aria-activedescendant); the dialog traps
  //    Tab across its focusable chrome (input / close / rail). ──
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isDescendantScrollKey(e.key)) {
      e.preventDefault();
      if (visibleOptions.length === 0) return;
      if (e.key === "ArrowDown")
        moveActive(
          activeIndex + 1 > visibleOptions.length - 1 ? 0 : activeIndex + 1,
        );
      else if (e.key === "ArrowUp")
        moveActive(
          activeIndex - 1 < 0 ? visibleOptions.length - 1 : activeIndex - 1,
        );
      else if (e.key === "Home") moveActive(0);
      else moveActive(visibleOptions.length - 1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const option = visibleOptions[activeIndex];
      if (!option) return;
      if ((e.metaKey || e.ctrlKey) && option.href) {
        window.open(option.href, "_blank", "noopener");
        return;
      }
      option.activate();
    }
  };

  const onDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closePalette();
      return;
    }
    if (e.key !== "Tab") return;
    const focusables =
      dialogRef.current?.querySelectorAll<HTMLElement>("input, button");
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  const activeOptionId =
    visibleOptions.length > 0
      ? `palette-opt-${visibleOptions[activeIndex]?.id}`
      : undefined;

  const RAILS: { id: PaletteRail; label: string }[] = [
    { id: "all", label: "All" },
    { id: "apps", label: "Capsules" },
    { id: "categories", label: "Categories" },
    { id: "surfaces", label: "Surfaces" },
    { id: "actions", label: "Actions" },
  ];

  let lastGroup: string | null = null;

  return createPortal(
    <div
      className="palette-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closePalette();
      }}
    >
      <div
        ref={dialogRef}
        className="palette-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t("Search")}
        onKeyDown={onDialogKeyDown}
      >
        <div className="palette-input-row">
          <Search size={17} aria-hidden />
          <input
            ref={inputRef}
            type="text"
            className="palette-input"
            placeholder={t("Search Capsules")}
            aria-label={t("Search Capsules")}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-results"
            aria-activedescendant={activeOptionId}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCategoryFilter(null);
              setActiveIndex(0);
            }}
            onKeyDown={onInputKeyDown}
            enterKeyHint="search"
          />
          <button
            type="button"
            className="palette-close"
            onClick={closePalette}
            aria-label={t("Close search")}
          >
            <X size={16} aria-hidden />
          </button>
        </div>
        <div className="palette-body">
          <nav className="palette-rail" aria-label={t("Search categories")}>
            {RAILS.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`palette-rail-btn ${rail === r.id ? "palette-rail-btn--active" : ""}`}
                onClick={() => setRail(r.id)}
              >
                {t(r.label)}
              </button>
            ))}
          </nav>
          <div
            ref={listRef}
            id="palette-results"
            className="palette-results"
            role="listbox"
            aria-label={t("Search results")}
          >
            {visibleOptions.length === 0 ? (
              <p className="palette-empty">
                {searching
                  ? t("Searching…")
                  : debouncedQuery
                    ? t("No apps match “{{value0}}”. Try another phrase or paste a GitHub repository URL.", { value0: debouncedQuery })
                    : rail === "categories"
                      ? t("No categories here yet.")
                      : t("Type to search apps, or paste a GitHub repository URL.")}
              </p>
            ) : (
              visibleOptions.map((option, index) => {
                const groupLabel =
                  option.group !== lastGroup ? option.group : null;
                lastGroup = option.group;
                return (
                  <div key={option.id}>
                    {groupLabel ? (
                      <div className="palette-group-label">{t(groupLabel)}</div>
                    ) : null}
                    <div
                      id={`palette-opt-${option.id}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      data-option-index={index}
                      className={`palette-option ${index === activeIndex ? "palette-option--active" : ""}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => option.activate()}
                    >
                      <span className="palette-option-icon" aria-hidden>
                        {option.iconUrl ? (
                          <img src={option.iconUrl} alt="" loading="lazy" />
                        ) : (
                          (option.icon ?? <GenericAppGlyph size={24} />)
                        )}
                      </span>
                      <span className="palette-option-text">
                        <span className="palette-option-title">
                          {option.group === "Actions" ? t(option.title) : option.title}
                        </span>
                        {option.sub ? (
                          <span className="palette-option-sub">
                            {option.group === "Surfaces" || option.group === "Categories" || gitRepo ? t(option.sub) : option.sub}
                          </span>
                        ) : null}
                      </span>
                      {option.mono ? (
                        <span className="palette-option-mono">
                          {option.mono}
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
